import { inject, Injectable } from '@angular/core';
import { Job, JobItem, JobSearchDto } from '@app/core/models/job.interface';
import { delay, Observable, of } from 'rxjs';
import { HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import { parametersFromPath } from '@app/core/interceptors/mock-api.interceptor';
import { CustomerMocksService } from '@app/core/services/mocks/customer-mocks.service';
import { VehicleMocksService } from '@app/core/services/mocks/vehicle-mocks.service';
import { faker } from '@faker-js/faker';
import { ApiResponse } from '@app/core/adaptors/dto';

@Injectable({ providedIn: 'root' })
export class JobMocksService {
  private readonly STORAGE_KEY = 'mock_jobs';
  private readonly ITEMS_STORAGE_KEY = 'mock_job_items';
  private customerMocksService = inject(CustomerMocksService);
  private vehicleMocksService = inject(VehicleMocksService);

  private jobs: Job[] = [];
  private jobItems = new Map<number, JobItem[]>();
  private nextItemId = 1;

  constructor() {
    this.loadJobsFromStorage();
    this.loadJobItemsFromStorage();
  }

  handleMockRequest(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    // Check if this is a job items request (URL contains /items)
    if (req.url.includes('/items')) {
      return this.handleJobItemRequest(req);
    }

    const { resourceId } = parametersFromPath(req, 'jobs');
    if (req.method === 'GET') {
      if (resourceId) {
        throw new Error('Not implemented yet');
      } else {
        const url = new URL(req.url);
        const params = url.searchParams;
        const searchDto: JobSearchDto = {
          page: parseInt(params.get('page') || '1', 10),
          limit: parseInt(params.get('limit') || '10', 10),
          jobId: params.get('jobId') ? parseInt(params.get('jobId')!, 10) : undefined,
          query: params.get('query') || undefined,
          customerId: params.get('customerId')
            ? parseInt(params.get('customerId')!, 10)
            : undefined,
          status: (params.get('status') as any) || undefined,
          technicianId: params.get('technicianId')
            ? parseInt(params.get('technicianId')!, 10)
            : undefined,
        };
        return this.getJobs(searchDto);
      }
    } else {
      throw new Error(`Unsupported request: ${req.method} ${req.url}`);
    }
  }

  private handleJobItemRequest(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    const { resourceId: jobId } = parametersFromPath(req, 'jobs');
    const { resourceId: itemId } = parametersFromPath(req, 'items');

    if (!jobId) {
      throw new Error('Job ID is required for job items');
    }

    switch (req.method) {
      case 'GET':
        return this.getJobItems(jobId);
      case 'POST':
        return this.createJobItem(jobId, req.body);
      case 'PUT':
        if (!itemId) throw new Error('Item ID is required for update');
        return this.updateJobItem(jobId, itemId, req.body);
      case 'DELETE':
        if (!itemId) throw new Error('Item ID is required for delete');
        return this.deleteJobItem(jobId, itemId);
      default:
        throw new Error(`Unsupported request: ${req.method} ${req.url}`);
    }
  }

  private getJobItems(jobId: number): Observable<HttpEvent<any>> {
    const items = this.jobItems.get(jobId) || [];
    const res: ApiResponse<JobItem> = {
      data: items,
      total: items.length,
      page: 1,
      limit: items.length,
      totalPages: 1,
    };
    return this.mapToResponse(res, 200);
  }

  private createJobItem(jobId: number, body: any): Observable<HttpEvent<any>> {
    const newItem: JobItem = {
      id: this.nextItemId++,
      ...body,
    };
    const items = this.jobItems.get(jobId) || [];
    items.push(newItem);
    this.jobItems.set(jobId, items);
    this.saveJobItemsToStorage();
    return this.mapToResponse(newItem, 201);
  }

  private updateJobItem(jobId: number, itemId: number, body: any): Observable<HttpEvent<any>> {
    const items = this.jobItems.get(jobId) || [];
    const index = items.findIndex((i) => i.id === itemId);
    if (index !== -1) {
      items[index] = { ...items[index], ...body, id: itemId };
      this.jobItems.set(jobId, items);
      this.saveJobItemsToStorage();
      return this.mapToResponse(items[index], 200);
    }
    return this.mapToResponse({ error: 'Item not found' }, 404);
  }

  private deleteJobItem(jobId: number, itemId: number): Observable<HttpEvent<any>> {
    const items = this.jobItems.get(jobId) || [];
    const filtered = items.filter((i) => i.id !== itemId);
    this.jobItems.set(jobId, filtered);
    this.saveJobItemsToStorage();
    return this.mapToResponse(null, 200);
  }

  private loadJobItemsFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.ITEMS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.jobItems = new Map(parsed.entries);
        this.nextItemId = parsed.nextId || 1;
      }
    } catch (error) {
      console.error('Error loading job items from localStorage:', error);
      this.jobItems = new Map();
    }
  }

  private saveJobItemsToStorage(): void {
    try {
      const serializable = {
        entries: Array.from(this.jobItems.entries()),
        nextId: this.nextItemId,
      };
      localStorage.setItem(this.ITEMS_STORAGE_KEY, JSON.stringify(serializable));
    } catch (error) {
      console.error('Error saving job items to localStorage:', error);
    }
  }

  getJobs(searchDto: JobSearchDto): Observable<HttpEvent<any>> {
    if (this.jobs.length === 0) {
      this.generateMockJobs();
      this.saveJobsToStorage();
    }

    const { limit, page } = searchDto;

    let filtered = [...this.jobs];

    if (searchDto.jobId) {
      filtered = filtered.filter((job) => job.id === searchDto.jobId);
    }
    if (searchDto.query) {
      const q = searchDto.query.toLowerCase();
      filtered = filtered.filter(
        (job) =>
          job.id.toString().includes(q) ||
          (job.customerName && job.customerName.toLowerCase().includes(q)) ||
          (job.vehicleRego && job.vehicleRego.toLowerCase().includes(q)) ||
          (job.technicianName && job.technicianName.toLowerCase().includes(q)) ||
          (job.description && job.description.toLowerCase().includes(q))
      );
    }
    if (searchDto.customerId) {
      filtered = filtered.filter((job) => job.customerId === searchDto.customerId);
    }
    if (searchDto.status) {
      filtered = filtered.filter((job) => job.status === searchDto.status);
    }
    if (searchDto.technicianId) {
      filtered = filtered.filter((job) => job.technicianId === searchDto.technicianId);
    }

    const data = filtered.slice((page - 1) * limit, page * limit);
    const res: ApiResponse<Job> = {
      data,
      total: filtered.length,
      page,
      limit,
      totalPages: Math.ceil(filtered.length / limit),
    };
    return this.mapToResponse(res, 200);
  }

  private loadJobsFromStorage() {
    try {
      const storedJobs = localStorage.getItem(this.STORAGE_KEY);
      if (storedJobs) {
        this.jobs = JSON.parse(storedJobs);
        console.log(`Loaded ${this.jobs.length} jobs from localStorage`);
      }
    } catch (error) {
      console.error('Error loading customers from localStorage:', error);
      this.jobs = [];
    }
  }

  private saveJobsToStorage(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.jobs));
      console.log(`Saved ${this.jobs.length} jobs to localStorage`);
    } catch (error) {
      console.error('Error saving jobs to localStorage:', error);
    }
  }

  private mapToResponse<T>(res: T, statusCode: number): Observable<HttpEvent<T>> {
    return of(
      new HttpResponse({
        status: statusCode,
        body: res,
      })
    ).pipe(
      delay(500) // Simulate network delay
    );
  }

  private generateMockJobs() {
    // Generate mock jobs
    for (let i = 1; i <= 100; i++) {
      this.jobs.push(this.newJob(i));
    }
  }

  private newJob(id: number): Job {
    const randomCustomer = this.getRandomCustomer();
    const randomVehicle = this.getRandomVehicle();
    const randomTechnicianId = faker.number.int({ min: 1, max: 10 });
    const randomJobSourceId = faker.number.int({ min: 1, max: 5 });
    const technicianFirstName = faker.person.firstName();
    const technicianLastName = faker.person.lastName();
    const startDate = faker.date.future();
    const endDate = faker.date.soon({ days: 7, refDate: startDate });

    return {
      id,
      tenantId: 1,
      jobSourceId: randomJobSourceId,
      jobSourceCode: faker.string.alphanumeric({ length: 4 }).toUpperCase(),
      customerId: randomCustomer.id,
      customerName: randomCustomer.name,
      vehicleId: randomVehicle.id!,
      vehicleRego: randomVehicle.rego,
      vehicleMake: randomVehicle.make || null,
      vehicleModel: randomVehicle.model || null,
      technicianId: randomTechnicianId,
      technicianCode: `TECH${randomTechnicianId.toString().padStart(3, '0')}`,
      technicianName: `${technicianFirstName} ${technicianLastName}`,
      status: faker.helpers.arrayElement(['New', 'Done', 'Cancelled']),
      startDate: startDate,
      endDate: endDate,
      startTime: faker.number.int({ min: 8, max: 17 }),
      dueTime: faker.number.int({ min: 8, max: 17 }),
      odometerReading: faker.number.int({ min: 0, max: 300000 }),
      description: faker.lorem.sentence({ min: 5, max: 10 }),
      internalNotes:
        faker.helpers.maybe(() => faker.lorem.paragraph(), { probability: 0.5 }) || null,
      customerNotes:
        faker.helpers.maybe(() => faker.lorem.paragraph(), { probability: 0.3 }) || null,
      jobCardPrinted: faker.datatype.boolean(),
      createdAt: faker.date.past(),
      updatedAt: faker.date.recent(),
      createdBy: faker.number.int({ min: 1, max: 5 }),
      updatedBy: faker.number.int({ min: 1, max: 5 }),
    };
  }

  private getRandomCustomer() {
    // Get mock customers synchronously from the customer service
    const customers = this.customerMocksService.getMockCustomers();
    return faker.helpers.arrayElement(customers);
  }

  private getRandomVehicle() {
    // Get mock customers synchronously from the customer service
    const vehicles = this.vehicleMocksService.getMockVehicles();

    return faker.helpers.arrayElement(vehicles);
  }
}
